import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auditService } from '../auditService';
import type { RetentionUnit } from '../../../types/retention';

export function useAuditLogs(page = 1, limit = 50, search?: string) {
  return useQuery({
    queryKey: ['auditLogs', page, limit, search],
    queryFn: () => auditService.getAuditLogs(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useDeleteAuditLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => auditService.deleteAuditLog(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

/** Bulk-delete audit logs. Renders its own toast, so the global one is hidden. */
export function useBulkDeleteAuditLogs() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => auditService.bulkDeleteAuditLogs(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

/** Preview how many logs a retention purge would remove (drives the modal copy). */
export function useAuditPurgePreview(
  value: number,
  unit: RetentionUnit,
  enabled = true,
) {
  return useQuery({
    queryKey: ['auditLogs', 'purgePreview', value, unit],
    queryFn: () => auditService.previewPurgeAuditLogs(value, unit),
    enabled: enabled && value > 0,
    staleTime: 0,
    retry: false,
  });
}

/** Purge (permanently delete) audit logs older than the retention window. */
export function usePurgeAuditLogs() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({ value, unit }: { value: number; unit: RetentionUnit }) =>
      auditService.purgeAuditLogs(value, unit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}
